import { useState } from "react";
import { fmtUSD } from "@/lib/api";
import { Trophy, Clock, AlertTriangle, Gauge } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

const semaforo = (d) => {
  if (d <= 30) return { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", label: "Al día" };
  if (d <= 60) return { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", label: "Atención" };
  return { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50", label: "Riesgo" };
};
const medals = ["text-amber-500", "text-slate-400", "text-orange-600"];
const idxColor = (n) => (n >= 75 ? "#16a34a" : n >= 50 ? "#d97706" : "#dc2626");
const clamp = (n, lo, hi) => Math.max(lo, Math.min(n, hi));

// Índice de gestión (0–100) · SOLO con datos que ya llegan. Si no hay desglose
// por antigüedad (+90 días) o valor de pagarés (+60 días) se usa 100 y se avisa.
function calcIndice(v, incluirPagares) {
  const tc = v.total_cartera || 0;
  const vv = v.valor_vencido || 0;
  const cm = v.cobrado_mes || 0;
  const m90 = v.vencido_mas_90_dias ?? v.vencido_90 ?? v.aging_mas_90 ??
    (v.tramos ? (v.tramos["+90"] ?? v.tramos.mas_90) : undefined);
  const hasAging = m90 !== null && m90 !== undefined;
  const vpp = v.valor_pagares_pendientes ?? v.pagares_pendientes_usd ?? v.valor_pagares ?? undefined;
  const vp60 = v.valor_pagares_mas_60_dias ?? v.pagares_mas_60_usd ?? v.valor_pagares_60 ?? undefined;
  const hasPag = vpp !== null && vpp !== undefined && vp60 !== null && vp60 !== undefined;
  const pv = tc > 0 ? vv / tc : 0;
  const s1 = 100 * (1 - Math.min(pv / 0.5, 1));
  const s2 = vv === 0 ? 100 : (hasAging ? clamp(100 * (1 - m90 / vv), 0, 100) : 100);
  const s3 = cm + vv === 0 ? 100 : 100 * Math.min(cm / (cm + vv), 1);
  const s4 = vpp === 0 ? 100 : (hasPag ? clamp(100 * (1 - vp60 / vpp), 0, 100) : 100);
  const indice = incluirPagares
    ? clamp(0.40 * s1 + 0.20 * s2 + 0.20 * s3 + 0.20 * s4, 0, 100)
    : clamp(0.50 * s1 + 0.25 * s2 + 0.25 * s3, 0, 100);
  return {
    s1, s2, s3, s4, indice,
    sinAntiguedad: vv > 0 && !hasAging,
    sinPagares: incluirPagares && vpp !== 0 && !hasPag,
  };
}

export default function RankingVendedores({ resumen, isAdmin }) {
  const reduced = useReducedMotion();
  const [incluirPagares, setIncluirPagares] = useState(false);
  const list = resumen?.por_vendedor || [];
  if (list.length < 1) return null;

  const cobranzas = [...list].sort((a, b) => b.cobrado_mes - a.cobrado_mes);
  const maxCobro = Math.max(1, ...cobranzas.map((v) => v.cobrado_mes));
  const dias = [...list].sort((a, b) => b.dias_pendientes_max - a.dias_pendientes_max || b.dias_pendientes_prom - a.dias_pendientes_prom);
  const deuda = [...list].sort((a, b) => b.valor_vencido - a.valor_vencido || b.dias_atraso_max - a.dias_atraso_max);
  const indice = list.map((v) => ({ ...v, ...calcIndice(v, incluirPagares) })).sort((a, b) => b.indice - a.indice);

  return (
    <section data-testid="ranking-vendedores" className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 sm:px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-transparent flex items-center gap-2">
        <Trophy className="h-5 w-5 text-emerald-600" />
        <div>
          <h3 className="font-heading text-lg font-bold text-emerald-900 leading-tight">Ranking y Desempeño de Vendedores</h3>
          <p className="text-xs text-slate-500">Cobranzas del mes y semáforo de gestión de pagarés</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 p-4 sm:p-5">
        <div data-testid="ranking-cobranzas">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
            <Trophy className="h-4 w-4 text-amber-500" /> Cobranzas del mes
          </div>
          <div className="space-y-3">
            {cobranzas.map((v, i) => (
              <div key={v.vendedor} data-testid="ranking-cobranzas-row" className="flex items-center gap-3">
                <span className={`w-5 text-center font-bold text-sm ${medals[i] || "text-slate-300"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-800 truncate">{v.vendedor}</span>
                    <span className="text-sm font-mono font-semibold text-emerald-700 tabular-nums whitespace-nowrap">{fmtUSD(v.cobrado_mes)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${Math.max(4, (v.cobrado_mes / maxCobro) * 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="ranking-dias">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
            <Clock className="h-4 w-4 text-emerald-600" /> Semáforo de pagarés · días desde emisión (máx.)
          </div>
          <div className="space-y-2">
            {dias.map((v, i) => {
              const s = semaforo(v.dias_pendientes_max);
              return (
                <div key={v.vendedor} data-testid="ranking-dias-row" className={`flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 ${s.bg}`}>
                  <span className={`w-5 text-center font-bold text-sm ${i === 0 && v.dias_pendientes_max > 0 ? "text-red-600" : "text-slate-400"}`}>{i + 1}</span>
                  <span className={`h-2.5 w-2.5 rounded-full ${s.dot} shrink-0`} />
                  <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">{v.vendedor}</span>
                  <span className={`text-sm font-mono font-bold tabular-nums ${s.text}`}>{v.dias_pendientes_max} d</span>
                  <span className={`text-[10px] font-semibold uppercase ${s.text} hidden sm:inline`}>{s.label}</span>
                  <span className="text-[11px] text-slate-400 whitespace-nowrap">{v.pagares_pendientes} pend.</span>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> ≤30 d</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> 31–60 d</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> +60 d</span>
          </p>
        </div>
      </div>

      <div data-testid="ranking-indice" className="border-t border-slate-100 px-4 sm:px-5 py-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Gauge className="h-4 w-4 text-emerald-600" /> Índice de gestión · 0 a 100
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={incluirPagares}
            data-testid="indice-toggle-pagares"
            onClick={() => setIncluirPagares((p) => !p)}
            className="inline-flex items-center gap-2 text-xs font-medium text-slate-600 shrink-0"
          >
            <span className={`relative h-5 w-9 rounded-full transition-colors ${incluirPagares ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${incluirPagares ? "left-[18px]" : "left-0.5"}`} />
            </span>
            Incluir pagarés
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mb-3">
          {incluirPagares
            ? "Morosidad (40%) · antigüedad (20%) · recupero (20%) · pagarés (20%) · mayor es mejor"
            : "Morosidad (50%) · antigüedad del vencido (25%) · recupero del mes (25%) · mayor es mejor"}
        </p>
        <div className="space-y-2">
          {indice.map((v, i) => {
            const col = idxColor(v.indice);
            const val = Math.round(v.indice);
            return (
              <div key={v.vendedor} data-testid="ranking-indice-row" className="group relative flex items-center gap-3">
                <span className="w-5 text-center font-bold text-sm text-slate-400">{i + 1}</span>
                <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">
                  {v.vendedor}
                  {v.sinAntiguedad && (
                    <span data-testid="indice-sin-antiguedad" className="ml-2 text-[10px] font-medium text-amber-600 whitespace-nowrap">
                      sin dato de antigüedad
                    </span>
                  )}
                  {v.sinPagares && (
                    <span data-testid="indice-sin-pagares" className="ml-2 text-[10px] font-medium text-amber-600 whitespace-nowrap">
                      sin dato de pagarés
                    </span>
                  )}
                </span>
                <div className="w-28 sm:w-40 h-2 rounded-full bg-slate-100 overflow-hidden shrink-0">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: col }}
                    initial={reduced ? false : { width: 0 }}
                    animate={{ width: `${Math.max(2, val)}%` }}
                    transition={reduced ? { duration: 0 } : { duration: 0.8, ease: "easeOut" }}
                  />
                </div>
                <span className="text-sm font-mono font-bold tabular-nums w-9 text-right shrink-0" style={{ color: col }}>{val}</span>
                <div className="pointer-events-none absolute right-12 bottom-6 z-20 hidden group-hover:block w-56 rounded-lg bg-slate-900 text-white text-[11px] leading-relaxed px-3 py-2 shadow-lg">
                  <div className="font-semibold mb-1">{v.vendedor} · índice {val}</div>
                  <div>s1 · morosidad ({incluirPagares ? "40%" : "50%"}): <b>{v.s1.toFixed(1)}</b></div>
                  <div>s2 · antigüedad ({incluirPagares ? "20%" : "25%"}): <b>{v.s2.toFixed(1)}</b>{v.sinAntiguedad ? " (sin dato → 100)" : ""}</div>
                  <div>s3 · recupero ({incluirPagares ? "20%" : "25%"}): <b>{v.s3.toFixed(1)}</b></div>
                  <div>s4 · pagarés {incluirPagares ? "(20%)" : "(no incluido)"}: <b>{v.s4.toFixed(1)}</b>{v.sinPagares ? " (sin dato → 100)" : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isAdmin && (
        <div data-testid="ranking-deuda-vencida" className="border-t border-slate-100 px-4 sm:px-5 py-5">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
            <AlertTriangle className="h-4 w-4 text-red-600" /> Deuda vencida · solo administradores
          </div>
          <div className="space-y-2">
            {deuda.map((v, i) => (
              <div key={v.vendedor} data-testid="ranking-deuda-row" className={`flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 ${i === 0 && v.valor_vencido > 0 ? "bg-red-50" : "bg-white"}`}>
                <span className={`w-5 text-center font-bold text-sm ${i === 0 && v.valor_vencido > 0 ? "text-red-600" : "text-slate-400"}`}>{i + 1}</span>
                <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">{v.vendedor}</span>
                <span className="text-sm font-mono font-bold tabular-nums text-red-600 whitespace-nowrap">{fmtUSD(v.valor_vencido)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Ordenado por mayor deuda vencida.</p>
        </div>
      )}
    </section>
  );
}
